import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/brand/Logo";
import { StepCard, RemovedStep, MissingStep } from "@/components/advisor/StepCard";
import { ExtraCard } from "@/components/advisor/ExtraCard";
import { MomentSwitch, MomentBanner, MomentNext } from "@/components/advisor/MomentSwitch";
import { AlternativesSheet } from "@/components/advisor/AlternativesSheet";
import { AddonsSheet } from "@/components/advisor/AddonsSheet";
import { ConsultantChat } from "@/components/advisor/ConsultantChat";
import { ShareRoutine } from "@/components/advisor/ShareRoutine";
import { RemoveChoiceDialog } from "@/components/advisor/RemoveChoiceDialog";
import { InstallPrompt } from "@/components/advisor/InstallPrompt";
import { api, euro } from "@/lib/api";
import { flow, getSessionId } from "@/lib/flow";
import { buildCartUrl } from "@/lib/shopify";
import { getUtm, track } from "@/lib/tracking";

export default function Routine() {
  const navigate = useNavigate();
  const [result, setResult] = useState(() => flow.result());
  const [moment, setMoment] = useState("day");
  const [selection, setSelection] = useState(flow.selection());
  const [sheet, setSheet] = useState(null);
  const [addonSheet, setAddonSheet] = useState(null);
  const [extras, setExtras] = useState(flow.extras());
  const [config, setConfig] = useState(null);
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    if (!result) { navigate("/", { replace: true }); return; }
    api.get("/advisor/config").then((r) => {
      setConfig(r.data);
      const answers = flow.answers();
      const stale = r.data.engine_version && result.engine_version !== r.data.engine_version && Object.keys(answers).length > 0;
      if (!stale) return;
      // routine salvata nel browser con una versione precedente del motore: la rigeneriamo con le stesse risposte
      api.post("/advisor/generate", { session_id: getSessionId(), answers, utm: getUtm() }).then((g) => {
        flow.setResult(g.data); flow.setSelection({}); flow.setExtras([]);
        setSelection({}); setExtras([]); setResult(g.data);
        toast.success("Abbiamo aggiornato la tua routine con le ultime novità del catalogo");
      }).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);
  useEffect(() => { flow.setSelection(selection); }, [selection]);
  useEffect(() => { flow.setExtras(extras); }, [extras]);

  const chosen = (m, entry) => {
    const v = selection[m]?.[entry.step_key];
    if (entry.extra) return v || null;  // extra settimanale: opt-in, mai nel carrello finché non viene aggiunto
    return v === undefined ? entry.primary_id : v;
  };

  const cartItems = useMemo(() => {
    if (!result) return [];
    const ids = new Set();
    ["day", "night"].forEach((m) => result.routine[m].forEach((e) => { const id = chosen(m, e); if (id) ids.add(id); }));
    extras.forEach((id) => ids.add(id));
    return [...ids].map((id) => result.products[id]).filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, extras, result]);

  if (!result) return null;
  const { routine, products, profile } = result;
  const entries = routine[moment];
  const countOf = (list) => ({ steps: list.filter((e) => !e.extra).length, extras: list.filter((e) => e.extra).length });
  const total = cartItems.reduce((s, p) => s + (p.price || 0), 0);
  const validItems = cartItems.filter((p) => p.shopify_variant_id);
  const domain = config?.shop_domain;
  const knownVariants = new Set(Object.values(products).map((p) => String(p.shopify_variant_id)).filter(Boolean));
  const cartUrl = buildCartUrl({ items: validItems, domain, sessionId: getSessionId(), utm: getUtm(), mode: config?.cart_mode, previous: flow.pushed().filter((v) => knownVariants.has(String(v))) });
  const shareUrl = cartUrl;

  const setChoice = (m, key, val) => setSelection((s) => ({ ...s, [m]: { ...s[m], [key]: val } }));

  const removeFrom = (pid, moments) => {
    const p = products[pid];
    setSelection((s) => {
      const next = { ...s };
      moments.forEach((m) => { const e = routine[m].find((x) => chosen(m, x) === pid); if (e) next[m] = { ...next[m], [e.step_key]: null }; });
      return next;
    });
    moments.forEach((m) => track("product_removed", { product_id: p?.id, product_name: p?.name, step: routine[m].find((x) => chosen(m, x) === pid)?.step_key, moment: m }));
    const other = moment === "day" ? "night" : "day";
    if (moments.length === 2) toast.success(`${p?.name} tolto da entrambi i rituali e dal carrello`);
    else if (moments[0] !== moment) toast.success(`${p?.name} tolto dal rituale ${other === "day" ? "del giorno" : "della sera"}: resta qui e nel carrello`);
    else if (routine[other].some((x) => chosen(other, x) === pid)) toast.success(`${p?.name} resta nel carrello perché è ancora nel rituale ${other === "day" ? "del giorno" : "della sera"}`);
    setRemoving(null);
  };

  const remove = (entry) => {
    const pid = chosen(moment, entry);
    const other = moment === "day" ? "night" : "day";
    if (routine[other].some((e) => chosen(other, e) === pid)) setRemoving(pid);
    else removeFrom(pid, [moment]);
  };

  const choose = (id) => {
    const p = products[id];
    setChoice(moment, sheet.step_key, id);
    track(sheet.extra ? "extra_added" : "alternative_selected", { product_id: id, product_name: p?.name, step: sheet.step_key, moment });
    setSheet(null);
    toast.success(`${p?.name} aggiunto alla tua routine`);
  };

  const toggleWeeklyExtra = (entry, add) => {
    const id = add ? entry.primary_id : chosen(moment, entry);
    const p = products[id];
    setChoice(moment, entry.step_key, add ? entry.primary_id : null);
    track(add ? "extra_added" : "extra_removed", { product_id: id, product_name: p?.name, step: entry.step_key, moment });
    if (add) toast.success(`${p?.name} aggiunto alla tua routine: lo trovi nel carrello`);
  };

  const toggleExtra = (id) => {
    const p = products[id];
    const adding = !extras.includes(id);
    setExtras((x) => (adding ? [...x, id] : x.filter((i) => i !== id)));
    track(adding ? "addon_added" : "addon_removed", { product_id: id, product_name: p?.name, step: addonSheet?.step_key });
    if (adding) toast.success(`${p?.name} aggiunto alla tua routine`);
  };

  const goToCart = () => {
    if (!cartUrl) return;
    validItems.forEach((p) => track("product_selected", { product_id: p.id, product_name: p.name }));
    track("go_to_cart", { total, items: validItems.length, url: cartUrl });
    flow.setPushed(validItems.map((p) => String(p.shopify_variant_id)));
    setTimeout(() => { window.location.href = cartUrl; }, 250);
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] pb-36" data-testid="routine-page">
      <Header right={<button data-testid="routine-view-profile-btn" className="fs-btn-ghost" onClick={() => navigate("/profilo")}>Il mio profilo</button>} />
      <main className="max-w-3xl mx-auto px-6 sm:px-10">
        <p className="fs-eyebrow pt-4">La tua consulenza · {profile.skin_type?.name}</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-[1.1] tracking-tight mt-5">Il tuo rituale, passo dopo passo.</h1>
        <p className="text-[15px] text-[#595652] mt-5 font-light leading-relaxed max-w-xl">
          Abbiamo scelto ogni prodotto in base al tuo profilo. Puoi rimuovere uno step o scegliere un'alternativa: ti proponiamo solo formule compatibili con la tua pelle.
        </p>
        <MomentSwitch moment={moment} counts={{ day: countOf(routine.day), night: countOf(routine.night) }} onChange={setMoment} />
        <MomentBanner moment={moment} />
        <section className="mt-2" data-testid={`routine-list-${moment}`}>
          {entries.map((entry, i) => {
            const id = chosen(moment, entry);
            if (entry.extra) {
              const pid = id || entry.primary_id;
              return products[pid] ? <ExtraCard key={`${moment}-${entry.step_key}`} entry={entry} product={products[pid]} added={!!id} skinName={profile.skin_type?.name}
                onAdd={() => toggleWeeklyExtra(entry, true)} onRemove={() => toggleWeeklyExtra(entry, false)} onAlternatives={() => setSheet(entry)} /> : null;
            }
            const num = String(entries.slice(0, i + 1).filter((e) => !e.extra).length).padStart(2, "0");
            if (entry.missing) return <MissingStep key={`${moment}-${entry.step_key}`} entry={entry} num={num} />;
            return id && products[id] ? (
              <StepCard key={`${moment}-${entry.step_key}`} entry={entry} product={products[id]} num={num} skinName={profile.skin_type?.name}
                onAlternatives={() => setSheet(entry)} onRemove={() => remove(entry)} onAddons={() => setAddonSheet(entry)}
                extrasCount={(entry.addon_ids || []).filter((a) => extras.includes(a)).length} />
            ) : (
              <RemovedStep key={`${moment}-${entry.step_key}`} entry={entry} num={num} onRestore={() => setChoice(moment, entry.step_key, undefined)} />
            );
          })}
          {!entries.length && <p className="py-10 text-[#8C8275]">Nessuno step disponibile per questo momento.</p>}
        </section>
        <MomentNext moment={moment} onChange={setMoment} />
        <ConsultantChat profile={profile} routine={routine} chosen={chosen} />
        <ShareRoutine profile={profile} routine={routine} products={products} chosen={chosen} extras={extras} cartUrl={shareUrl} total={total} cartItems={cartItems} waCloud={!!config?.wa_cloud_enabled} />
        <InstallPrompt />
      </main>
      <div className="fixed bottom-0 inset-x-0 z-40 bg-[#FAF8F5]/90 backdrop-blur-md border-t border-[#E6DFD5]" data-testid="cart-bar">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-[11px] tracking-[0.18em] uppercase text-[#8C8275]" data-testid="cart-items-count">{cartItems.length} prodotti · giorno e sera</p>
            <p className="font-serif text-2xl tabular-nums" data-testid="cart-total">{euro(total)}</p>
          </div>
          <button data-testid="buy-routine-final-btn" className="fs-btn !px-5 sm:!px-8" disabled={!cartItems.length} onClick={goToCart}>
            {config?.cart_mode === "cart" ? "Aggiungi al carrello" : "Acquista la mia routine"} <ArrowRight size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>
      <RemoveChoiceDialog open={!!removing} product={removing ? products[removing] : null} moment={moment} onChoose={(ms) => removeFrom(removing, ms)} onClose={() => setRemoving(null)} />
      <AlternativesSheet open={!!sheet} entry={sheet} products={products} currentId={sheet && chosen(moment, sheet)} onClose={() => setSheet(null)} onChoose={choose} />
      <AddonsSheet open={!!addonSheet} entry={addonSheet} products={products} extras={extras} onClose={() => setAddonSheet(null)} onToggle={toggleExtra}
        stepProduct={addonSheet ? products[chosen(moment, addonSheet)] : null} />
    </div>
  );
}
